import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Link2, Copy, Check, X, Clock, UserCheck, Ban, Plus,
  Mail, Shield, Building2, RefreshCw, Send, Calendar
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  ativo:    { label: "Ativo",    color: "oklch(0.55 0.15 145)" },
  usado:    { label: "Usado",    color: "oklch(0.55 0.05 240)" },
  expirado: { label: "Expirado", color: "oklch(0.55 0.05 30)" },
  revogado: { label: "Revogado", color: "oklch(0.55 0.15 25)" },
};

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

// ─── CreateInviteDialog ────────────────────────────────────────────────────────
function CreateInviteDialog({ open, onClose, clients }: {
  open: boolean;
  onClose: () => void;
  clients: { id: number; name: string }[];
}) {
  const utils = trpc.useUtils();
  const { session: authSession } = useAuth();
  // Bug histórico: CLIENT_AUTH_KEY do ClientLogin.tsx ("raz_client_session")
  // não é a key usada hoje pelo AuthContext ("socialhub_client_session").
  // localStorage.getItem(key errada) → null → botão "Gerar e enviar" ficava
  // sempre disabled mesmo logado. Trocado pra usar o hook useAuth direto.
  const session = authSession?.token ?? null;
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "viewer" as "admin" | "editor" | "viewer",
    clientIds: [] as number[],
    allClients: false,
    expiresInDays: 7,
  });
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [createdInviteId, setCreatedInviteId] = useState<number | null>(null);
  const [createdEmailStatus, setCreatedEmailStatus] = useState<string | null>(null);
  const [createdEmailError, setCreatedEmailError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMut = trpc.invites.create.useMutation({
    onSuccess: (data) => {
      utils.invites.list.invalidate();
      setCreatedLink(data.link);
      setCreatedInviteId((data as any).id ?? null);
      setCreatedEmailStatus((data as any).emailStatus ?? null);
      setCreatedEmailError((data as any).emailError ?? null);
      navigator.clipboard.writeText(data.link).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      // Toast diferente conforme email funcionou ou não
      const status = (data as any).emailStatus;
      if (status === "sent") toast.success("Convite criado e enviado por email. Link também copiado.");
      else if (status === "no_api_key") toast.info("Convite criado. Email desativado — copie o link e envie manualmente.");
      else toast.warning("Convite criado, mas email FALHOU. Envie o link manualmente (já copiado).");
    },
    onError: (err) => toast.error(err.message),
  });

  // Reenviar email do convite recém-criado (quando o primeiro send falhou)
  const resendModalMut = trpc.invites.resend.useMutation({
    onSuccess: (data) => {
      const status = (data as any).emailStatus;
      setCreatedEmailStatus(status);
      setCreatedEmailError((data as any).emailError ?? null);
      if (status === "sent") toast.success("Email reenviado com sucesso!");
      else if (status === "no_api_key") toast.info("Email continua desativado.");
      else toast.warning("Email falhou de novo — confira o domínio no Resend.");
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleClient = (id: number) => {
    setForm(f => ({
      ...f,
      clientIds: f.clientIds.includes(id) ? f.clientIds.filter(x => x !== id) : [...f.clientIds, id],
      allClients: false,
    }));
  };

  const handleClose = () => {
    setCreatedLink(null);
    setCreatedInviteId(null);
    setCreatedEmailStatus(null);
    setCreatedEmailError(null);
    setForm({ email: "", name: "", role: "viewer", clientIds: [], allClients: false, expiresInDays: 7 });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Gerar convite
          </DialogTitle>
        </DialogHeader>

        {createdLink ? (
          <div className="space-y-4 pt-2">
            {/* Status do envio por email */}
            {createdEmailStatus === "sent" && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 space-y-1">
                <p className="text-sm font-medium flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> Email enviado pra {form.email}
                </p>
                <p className="text-xs text-green-700/80">O destinatário deve receber em alguns segundos. Cheque o spam se não chegar.</p>
              </div>
            )}
            {createdEmailStatus === "no_api_key" && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-1">
                <p className="text-sm font-medium text-amber-800">⚠ Email desativado</p>
                <p className="text-xs text-amber-700">RESEND_API_KEY não está configurado. Copie o link abaixo e envie manualmente.</p>
              </div>
            )}
            {createdEmailStatus === "failed" && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                <p className="text-sm font-medium text-red-800">⚠ Email FALHOU</p>
                <p className="text-xs text-red-700">
                  Resend rejeitou o envio. Provavelmente domínio não verificado ou em modo testing.
                  Copie o link abaixo e envie manualmente (WhatsApp/Slack/etc), ou tente reenviar depois de corrigir o Resend.
                </p>
                {createdEmailError && (
                  <p className="text-[10px] text-red-600/80 font-mono break-all">{createdEmailError.slice(0, 200)}</p>
                )}
                {createdInviteId && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 border-red-300 text-red-700 hover:bg-red-100"
                    disabled={resendModalMut.isPending}
                    onClick={() => resendModalMut.mutate({ id: createdInviteId, origin: window.location.origin })}
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {resendModalMut.isPending ? "Reenviando…" : "Tentar reenviar email"}
                  </Button>
                )}
              </div>
            )}

            <div className="rounded-lg bg-muted/50 border p-4 space-y-2">
              <p className="text-sm font-medium flex items-center gap-2 text-foreground">
                <Link2 className="w-4 h-4" /> Link do convite
              </p>
              <p className="text-xs text-muted-foreground">Válido por {form.expiresInDays} dias. Link copiado pro clipboard automaticamente.</p>
              <div className="flex items-center gap-2 min-w-0">
                {/* min-w-0 no pai E no filho — sem isso, truncate não funciona dentro de flex (filho não encolhe abaixo do content width) */}
                <code className="text-xs bg-background border rounded px-2 py-1 flex-1 min-w-0 truncate block">{createdLink}</code>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 flex-shrink-0"
                  onClick={() => { navigator.clipboard.writeText(createdLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                  {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <div className="flex gap-2 pt-1">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Olá! Você foi convidado pro SocialHub. Acesse: ${createdLink}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline"
                >
                  Compartilhar via WhatsApp →
                </a>
                <a
                  href={`mailto:${form.email}?subject=${encodeURIComponent("Convite SocialHub")}&body=${encodeURIComponent(`Olá ${form.name},\n\nVocê foi convidado pro SocialHub.\nAcesse: ${createdLink}\n\nO link expira em ${form.expiresInDays} dias.`)}`}
                  className="text-[11px] text-muted-foreground hover:text-foreground underline"
                >
                  Abrir no email →
                </a>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleClose}>Fechar</Button>
              <Button className="flex-1 gap-1.5"
                onClick={() => { setCreatedLink(null); setCreatedEmailStatus(null); setCreatedEmailError(null); setForm({ email: "", name: "", role: "viewer", clientIds: [], allClients: false, expiresInDays: 7 }); }}>
                <Plus className="w-3.5 h-3.5" /> Novo convite
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Nome</label>
                <Input placeholder="Nome do contato" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Email</label>
                <Input type="email" placeholder="email@empresa.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Permissão</label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Visualizador</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Expira em</label>
                <Select value={String(form.expiresInDays)} onValueChange={v => setForm(f => ({ ...f, expiresInDays: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 dias</SelectItem>
                    <SelectItem value="7">7 dias</SelectItem>
                    <SelectItem value="14">14 dias</SelectItem>
                    <SelectItem value="30">30 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Acesso a clientes</label>
              <div className="border rounded-lg p-2 space-y-1 max-h-44 overflow-y-auto">
                <label className="flex items-center gap-2.5 p-1.5 rounded cursor-pointer hover:bg-muted/50 font-medium">
                  <Checkbox checked={form.allClients}
                    onCheckedChange={v => setForm(f => ({ ...f, allClients: !!v, clientIds: v ? clients.map(c => c.id) : [] }))} />
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-sm">Todos os clientes</span>
                </label>
                <div className="border-t pt-1 space-y-0.5">
                  {clients.map(c => (
                    <label key={c.id} className="flex items-center gap-2.5 p-1.5 rounded cursor-pointer hover:bg-muted/50">
                      <Checkbox checked={form.clientIds.includes(c.id)} onCheckedChange={() => toggleClient(c.id)} />
                      <span className="text-sm">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={handleClose}>Cancelar</Button>
              <Button className="flex-1 gap-1.5"
                disabled={!form.email || !form.name || !session || createMut.isPending}
                onClick={() => createMut.mutate({
                  email: form.email,
                  name: form.name,
                  role: form.role,
                  clientIds: form.clientIds.length > 0 ? form.clientIds : undefined,
                  expiresInDays: form.expiresInDays,
                  origin: window.location.origin,
                })}>
                {createMut.isPending ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Gerar e enviar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Convites() {
  const { session: authSession } = useAuth();
  const session = authSession?.token ?? "";
  const { data: invites = [], isLoading, refetch } = trpc.invites.list.useQuery(
    undefined,
    { enabled: !!session }
  );
  const { data: clients = [] } = trpc.clients.getMyClients.useQuery(
    undefined,
    { enabled: !!session }
  );
  const utils = trpc.useUtils();
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState<"todos" | "ativo" | "usado" | "expirado" | "revogado">("todos");
  const [copied, setCopied] = useState<number | null>(null);

  const revokeMut = trpc.invites.revoke.useMutation({
    onSuccess: () => { utils.invites.list.invalidate(); toast.success("Convite revogado"); },
    onError: (err) => toast.error(err.message),
  });

  const resendMut = trpc.invites.resend.useMutation({
    onSuccess: (data) => {
      const status = (data as any).emailStatus;
      if (status === "sent") toast.success("Convite reenviado por email!");
      else if (status === "no_api_key") toast.info("Email desativado — link copiado pra você compartilhar.");
      else toast.warning("Email falhou — link copiado pra envio manual.");
      navigator.clipboard.writeText(data.link).catch(() => {});
    },
    onError: (err) => toast.error(err.message),
  });

  const filtered = filter === "todos" ? invites : invites.filter(i => i.status === filter);
  const counts = {
    todos: invites.length,
    ativo: invites.filter(i => i.status === "ativo").length,
    usado: invites.filter(i => i.status === "usado").length,
    expirado: invites.filter(i => i.status === "expirado").length,
    revogado: invites.filter(i => i.status === "revogado").length,
  };

  const copyLink = (id: number, token: string) => {
    const link = `${window.location.origin}/aceitar-convite?token=${token}`;
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Link copiado!");
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
      <PageHeader
        title="Convites"
        subtitle="Gerencie links de convite para acesso à plataforma"
        action={
          <Button size="sm" className="gap-1.5"
            onClick={() => setCreateOpen(true)}>
            <Plus className="w-3.5 h-3.5" /> Gerar convite
          </Button>
        }
      />

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {(["todos", "ativo", "usado", "expirado", "revogado"] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f
                ? "bg-[var(--brand-soft)] text-[var(--brand-ink)]"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            }`}>
            {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
          </button>
        ))}
        <button onClick={() => refetch()} className="ml-auto p-1.5 rounded-full bg-muted/50 text-muted-foreground hover:bg-muted">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Invite list */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">
          <Link2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nenhum convite {filter !== "todos" ? filter : "criado"} ainda.</p>
          {filter === "todos" && (
            <Button size="sm" className="mt-4 gap-1.5"
              onClick={() => setCreateOpen(true)}>
              <Plus className="w-3.5 h-3.5" /> Gerar primeiro convite
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(inv => {
            const cfg = STATUS_CONFIG[inv.status] ?? STATUS_CONFIG.expirado;
            return (
              <div key={inv.id} className={`rounded-xl border bg-card p-4 transition-opacity ${inv.status !== "ativo" ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `${cfg.color}/0.15` }}>
                    {inv.status === "ativo" ? <Link2 className="w-4 h-4" style={{ color: cfg.color }} /> :
                     inv.status === "usado" ? <UserCheck className="w-4 h-4" style={{ color: cfg.color }} /> :
                     inv.status === "revogado" ? <Ban className="w-4 h-4" style={{ color: cfg.color }} /> :
                     <Clock className="w-4 h-4" style={{ color: cfg.color }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{inv.name ?? inv.email}</span>
                      <Badge className="text-[10px] px-1.5 py-0" style={{ background: `${cfg.color}/0.15`, color: cfg.color, border: "none" }}>
                        {cfg.label}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                        <Shield className="w-2.5 h-2.5" />
                        {inv.role === "admin" ? "Admin" : inv.role === "editor" ? "Editor" : "Visualizador"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{inv.email}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Criado em {formatDate(inv.createdAt)}
                      </span>
                      {inv.status === "ativo" && (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Clock className="w-3 h-3" />
                          Expira em {formatDate(inv.expiresAt)}
                        </span>
                      )}
                      {inv.usedAt && (
                        <span className="flex items-center gap-1 text-green-600">
                          <UserCheck className="w-3 h-3" />
                          Aceito em {formatDate(inv.usedAt)}
                        </span>
                      )}
                      {inv.clientIds && inv.clientIds.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {inv.clientIds.length} cliente{inv.clientIds.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  {inv.status === "ativo" && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Copiar link"
                        onClick={() => copyLink(inv.id, inv.token)}>
                        {copied === inv.id ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Reenviar email"
                        disabled={resendMut.isPending}
                        onClick={() => resendMut.mutate({ id: inv.id, origin: window.location.origin })}>
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                        title="Revogar convite"
                        disabled={revokeMut.isPending}
                        onClick={() => { if (confirm(`Revogar convite de ${inv.email}?`)) revokeMut.mutate({ id: inv.id }); }}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateInviteDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clients={clients}
      />
    </div>
  );
}
